// /verify-email?token=... — landing for the click-through from the
// verification email. We fire the GET request once on mount, then
// render pending / success / failure state.
//
// Restyled to match /sign-in's hero+card layout.

import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useVerifyEmail } from "@/lib/auth";
import { Icons, Rosette } from "@/components/icons";
import { AuthCanvas, HeroPane } from "./sign-up";

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmailPage,
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});

function VerifyEmailPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const search = Route.useSearch();
  const verify = useVerifyEmail();

  useEffect(() => {
    if (search.token && verify.isIdle) {
      verify.mutate(search.token);
    }
    // mount-once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.token]);

  return (
    <AuthCanvas>
      <HeroPane tr={tr} />
      <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
        <VerifyCardBody tr={tr} hasToken={!!search.token} state={verifyState(verify)} />
      </div>
    </AuthCanvas>
  );
}

type VState = "missing" | "pending" | "success" | "error";

function verifyState(v: ReturnType<typeof useVerifyEmail>): VState {
  if (v.isSuccess) return "success";
  if (v.isError) return "error";
  return "pending";
}

function VerifyCardBody({ tr, hasToken, state }: { tr: boolean; hasToken: boolean; state: VState }) {
  if (!hasToken) {
    return (
      <>
        <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Hata" : "Error"}</div>
        <h2 style={{ fontSize: 24, marginBottom: 12 }}>{tr ? "Bağlantı eksik." : "Missing token."}</h2>
        <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
          {tr
            ? "Bu bağlantı geçerli bir doğrulama tokeni içermiyor."
            : "This link doesn't contain a valid verification token."}
        </p>
        <Link
          to="/sign-in"
          className="btn btn-secondary"
          style={{ width: "100%", justifyContent: "center", padding: "11px 16px" }}
        >
          <Icons.ChevronLeft size={14} />
          {tr ? "Giriş sayfasına dön" : "Back to sign in"}
        </Link>
      </>
    );
  }

  if (state === "pending") {
    return (
      <>
        <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Doğrulanıyor" : "Verifying"}</div>
        <h2 style={{ fontSize: 24, marginBottom: 16 }}>
          {tr ? "E-postan doğrulanıyor…" : "Verifying your email…"}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
          <Rosette size={42} stroke="var(--accent)" opacity={0.7} spin />
          <p style={{ fontSize: 13, color: "var(--tx2)", margin: 0 }}>
            {tr ? "Birkaç saniye sürer." : "This only takes a moment."}
          </p>
        </div>
      </>
    );
  }

  if (state === "error") {
    return (
      <>
        <div className="eyebrow" style={{ marginBottom: 14, color: "var(--error)" }}>— {tr ? "Başarısız" : "Failed"}</div>
        <h2 style={{ fontSize: 24, marginBottom: 12 }}>
          {tr ? "Doğrulama başarısız." : "Verification failed."}
        </h2>
        <p style={{ fontSize: 14, color: "var(--tx)", marginBottom: 10, lineHeight: 1.55 }}>
          {tr
            ? "Bağlantının süresi dolmuş veya zaten kullanılmış olabilir."
            : "This link may have expired or already been used."}
        </p>
        <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
          {tr
            ? "Hesabına giriş yapmayı dene — bazı durumlarda doğrulanmış olabilir. Yoksa kayıt sırasında kullandığın e-postayla tekrar dene."
            : "Try signing in — your account may already be verified. Otherwise, sign up again with the same email."}
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <Link
            to="/sign-in"
            className="btn btn-primary"
            style={{ flex: 1, justifyContent: "center", padding: "11px 16px" }}
          >
            {tr ? "Giriş yap" : "Sign in"}
            <Icons.ArrowRight size={14} />
          </Link>
          <Link
            to="/sign-up"
            className="btn btn-secondary"
            style={{ flex: 1, justifyContent: "center", padding: "11px 16px" }}
          >
            {tr ? "Tekrar kayıt ol" : "Sign up again"}
          </Link>
        </div>
      </>
    );
  }

  // success
  return (
    <>
      <div className="eyebrow" style={{ marginBottom: 14, color: "var(--success)" }}>— {tr ? "Doğrulandı" : "Verified"}</div>
      <h2 style={{ fontSize: 24, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
        {tr ? "E-posta doğrulandı" : "Email verified"}
        <span style={{ color: "var(--success)", display: "inline-flex" }}>
          <Icons.Check size={22} />
        </span>
      </h2>
      <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 22, lineHeight: 1.55 }}>
        {tr
          ? "Hesabın aktif. Şimdi giriş yapıp ilk markanı ekleyebilirsin."
          : "Your account is active. Sign in and add your first brand."}
      </p>
      <Link
        to="/sign-in"
        className="btn btn-primary"
        style={{ width: "100%", justifyContent: "center", padding: "11px 16px" }}
      >
        {tr ? "Giriş yap" : "Sign in"}
        <Icons.ArrowRight size={14} />
      </Link>
    </>
  );
}
